import _get from 'lodash/get';
import { TFunction } from 'i18next';
import { Value, Values } from '@lib/interfaces/form';
import getFullName from '@lib/utils/getFullName';
import { SiteFieldNames } from '@lib/enums/fieldNames/siteFieldNames';
import getPhoneNumberValueFromQueryData from '@lib/utils/getPhoneNumberValueFromQueryData';
import { SitePointerShape } from '@lib/components/Map/enums';
import getColorOptions from '@lib/components/Chip/utils/colorEnum';
import removeTypenameDeep from '@lib/utils/removeTypenameDeep';
import { removeWhiteFromIconUrl } from 'routes/Sites/utils/addWhiteToIconUrl';

export default function getSiteDefaultValuesFromQueryData(args: {
  data?: Values;
  assigneeIds?: { id: string }[];
  t: TFunction<'translation', undefined>;
}): Values {
  const { data, assigneeIds, t } = args;
  const locationPhotoAttached = _get(data, 'locationPhotoAttached');
  const newUserIdValue = _get(data, 'userId', undefined);
  const sectorIdValue = _get(data, 'sector.id');
  const sectorName = _get(data, 'sector.name') as string;
  const sectorColor = _get(data, 'sector.color') as string;
  const sectorOption = sectorIdValue
    ? { value: sectorIdValue, label: sectorName, color: sectorColor }
    : null;
  const user = _get(data, 'user');
  const userIdValue = _get(user, 'id');
  const userFirstName = _get(data, 'user.firstName') as string;
  const userLastName = _get(data, 'user.lastName') as string;
  const userFullName = getFullName({
    firstName: userFirstName,
    lastName: userLastName,
  });
  const userId =
    newUserIdValue === undefined && userIdValue
      ? ({
          label: userFullName,
          value: userIdValue,
          data: user,
        } as Value)
      : newUserIdValue;
  const phoneNumber = getPhoneNumberValueFromQueryData(data);
  const locationPoint = _get(data, ['locationPoint', 'geojson']);
  const locationPointValue = locationPoint
    ? removeTypenameDeep(locationPoint)
    : {
        type: 'FeatureCollection',
        features: [],
      };

  const locationPointShapeValue = _get(locationPointValue, [
    'features',
    0,
    'properties',
    'shape',
  ]);
  const locationPointShape = {
    value: locationPointShapeValue || SitePointerShape.Circle,
  };
  const locationPointColorValue = _get(locationPointValue, [
    'features',
    0,
    'properties',
    'color',
  ]);
  const locationPointColor = {
    // `color` is a backend `ColorEnum`; default to the first color option.
    value: locationPointColorValue || getColorOptions({ t })[0].value,
  };
  const icon = _get(locationPointValue, ['features', 0, 'properties', 'icon']);
  const locationPointIcon = icon
    ? {
        fileTag: removeWhiteFromIconUrl(icon),
      }
    : null;
  const locationPointIconValue = locationPointIcon?.fileTag
    ? locationPointIcon
    : null;
  const locationPointAddressDetails = {
    value: _get(locationPointValue, [
      'features',
      0,
      'properties',
      'addressDetails',
    ]),
  };

  const userMemberships =
    assigneeIds && assigneeIds?.length > 0
      ? assigneeIds.map(({ id }) => ({ value: id }))
      : [];
  return {
    [SiteFieldNames.Name]: data?.name || '',
    [SiteFieldNames.Code]: data?.code || '',
    [SiteFieldNames.Description]: data?.description || '',
    [SiteFieldNames.Email]: data?.email || '',
    [SiteFieldNames.Address]: locationPointAddressDetails?.value || '',
    [SiteFieldNames.LocationPhotoAttached]: locationPhotoAttached,
    [SiteFieldNames.PhoneNumber]: phoneNumber,
    [SiteFieldNames.UserId]: userId,
    [SiteFieldNames.Sector]: sectorOption,
    [SiteFieldNames.UserMemberships]: userMemberships,
    [SiteFieldNames.SiteDetails]: null,
    [SiteFieldNames.ContactInfo]: null,
    [SiteFieldNames.LocationInfo]: null,
    [SiteFieldNames.LocationPhoto]: null,
    [SiteFieldNames.UserMembershipsTitle]: null,
    [SiteFieldNames.UserMembershipsList]: null,
    [SiteFieldNames.SectorWarning]: null,
    [SiteFieldNames.SitePointerDesign]: null,
    [SiteFieldNames.LocationPoint]: locationPointValue,
    [SiteFieldNames.PointerShape]: locationPointShape,
    [SiteFieldNames.PointerColor]: locationPointColor,
    [SiteFieldNames.PointerIconAttached]: locationPointIconValue,
    // For Create form
    [SiteFieldNames.Areas]: [],
    [SiteFieldNames.ConductCodeSections]: [],
  };
}
